import { NetworkStatus } from "@apollo/client";

import Box from "ds/components/Box";
import MissingDataBanner from "components/MissingDataBanner";
import usePolledQuery from "apollo/usePolledQuery";
import { PollingIntervalGroups } from "apollo/constants";
import Timestamp from "components/Timestamp";
import Typography from "ds/components/Typography";

import DashboardWidgetsEmptyStateRuns from "../EmptyStateRuns";
import DashboardWidgetsRunTile from "../RunTile";
import DashboardWidgetsRunsSkeleton from "../RunsSkeleton";
import { GET_RUNS_RECENTLY_APPROVED } from "./gql";
import { Widget } from "../../types";
import styles from "./styles.module.css";

// TODO: [dashboard] add limit to query once added to API
const LIMIT = 10;

const DashboardWidgetsApprovalPolicyEvaluations = () => {
  const { loading, data, error, refetch, networkStatus } = usePolledQuery(
    GET_RUNS_RECENTLY_APPROVED,
    {
      pollingGroup: PollingIntervalGroups.Dashboard,
    }
  );

  const showErrorBanner = !!error;

  const isLoading =
    (loading && !data?.metrics?.runsRecentlyApproved) || networkStatus === NetworkStatus.refetch;

  const runs = data?.metrics?.runsRecentlyApproved?.slice(0, LIMIT);
  let emptyState;

  const isEmpty = !isLoading && !runs?.length;

  const hasRuns = !!data?.metrics?.hasRuns;

  if (!hasRuns) {
    emptyState = (
      <DashboardWidgetsEmptyStateRuns showDocs widget={Widget.ApprovalPolicyEvaluations} />
    );
  }

  if (hasRuns && isEmpty) {
    emptyState = (
      <DashboardWidgetsEmptyStateRuns
        caption="No approval evaluations."
        widget={Widget.ApprovalPolicyEvaluations}
        onlyCaption
      />
    );
  }

  if (showErrorBanner) {
    return (
      <MissingDataBanner
        text="Couldn’t load runs data. Please try to refresh or come back later. "
        refreshHandler={refetch}
        fullWidth
      />
    );
  }

  if (isLoading) {
    return <DashboardWidgetsRunsSkeleton />;
  }

  return (
    <Box className={styles.titleContent}>
      <Box direction="column" gap="medium" fullWidth>
        {emptyState}

        {!emptyState &&
          runs?.map(({ runTile: { id, title, createdAt, stackTile }, approval }, i) => (
            <DashboardWidgetsRunTile
              key={i}
              id={id}
              title={title}
              stackTile={stackTile}
              caption={
                <Box fullWidth>
                  <Typography color="secondary" tag="span" variant="p-body3">
                    Committed on <Timestamp timestamp={createdAt} />
                  </Typography>
                </Box>
              }
              approval={approval}
              analyticsTitle="Approval evaluations"
            />
          ))}
      </Box>
    </Box>
  );
};

export default DashboardWidgetsApprovalPolicyEvaluations;
